import * as React from 'react';
import { usePathPattern } from '@/components/utils/usePathPattern';

import './page.scss';
import useMaintenanceGateway from '@/components/modules/maintenance/MaintenanceGateway';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import transferPropertiesToComponent from '@/page-components/ComponentData/transferPropertiesToComponent';
import { clearCache } from '@/page-components/utils/reselect-utils';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchElements } from '@/store/slices/dataElements';
import { clearRequestSourcesCache, fetchSources } from '@/store/slices/dataSources';
import PageLoader from '../page-loader';
import PageMetaTags from '../page-meta-tags';
import craftJsParser from '../utils/craftJsParser';
import { PageDataContext } from '../utils/PageDataProvider';
import parseDataSources from '../utils/parseDataSources';
import { FixedPageContentContext } from '../fixed-routes/fixed-page-content-context';

type PageProps = {
  id: string;
  tagProps?: {
    [key: string]: any;
  };
};

let prerenderInitialized = false;

const Page: React.FC<PageProps> = (props) => {
  const { id } = props;

  const pattern = usePathPattern();
  const dispatch = useAppDispatch();
  const config = useAppSelector((state) => state.templatesConfig);
  const authenticated = useAppSelector((state) => state.authentication && state.authentication.auth_type === 'user');
  const isAuthInProgress = useAppSelector((state) => state.authentication.in_progress);
  const authenticationToken = useAppSelector((state) => state.authentication.access_token);
  const dataSourcesLoaded = useAppSelector((state) => state.dataSources.loaded);
  const pagesOverrides = useAppSelector((state) => state.templatesConfig.pagesOverrides);
  const pageDataContext = React.useContext(PageDataContext);
  // Fixed React routes (nova) inject their content here in place of the
  // page's CraftJS tree, only when this Page is the one their layout slot mounts.
  const fixedContent = React.useContext(FixedPageContentContext);

  const [doneRender, setDoneRender] = React.useState<any>(null);

  const action = useMaintenanceGateway();

  const page = id ? config.pages?.[id] : null;
  let projectData = page?.projectData;

  if (action?.redirect && config?.setPages?.['/maintenance/default']?.page_id) {
    projectData = config.pages[config.setPages['/maintenance/default'].page_id].projectData;
  } else if (page && page.a && !authenticated && config.setPages && config.setPages['/login']) {
    if (config.setPages['/login'].page_layout_id) {
      projectData = config.pageLayouts[config.setPages['/login'].page_layout_id].projectData;
    } else {
      projectData = config.pages[config.setPages['/login'].page_id].projectData;
    }
  }

  React.useEffect(() => {
    clearRequestSourcesCache();
  }, [id]);

  React.useEffect(() => {
    if (!prerenderInitialized) {
      prerenderInitialized = true;
      const prerenderDelay = parseInt(window.config.prerenderDelay);
      if (window.config.prerenderDelay && !isNaN(prerenderDelay)) {
        setTimeout(() => {
          // @ts-ignore
          window.prerenderReady = true;
        }, prerenderDelay);
      }
    }
  }, []);

  React.useEffect(() => {
    if (authenticationToken && projectData) {
      const data = JSON.parse(projectData);
      const overrides = pagesOverrides[`${pattern}|${id}`];

      if (overrides && Object.keys(overrides).length) {
        transferPropertiesToComponent(data, overrides, true);
      }

      let [dataSources, dataElements]: any = parseDataSources({
        craftState: data,
        type: 'Page',
        id,
      });

      if (pageDataContext?.pageLayoutId && config.pageLayouts[pageDataContext.pageLayoutId]) {
        const [ds, de] = parseDataSources({
          craftState: config.pageLayouts[pageDataContext.pageLayoutId].projectData,
          type: 'PageLayout',
          id: pageDataContext.pageLayoutId,
          lvl: 1,
        });

        const newDs: any = {};
        ds.forEach((d) => {
          newDs[d] = true;
        });
        dataSources.forEach((d: any) => {
          newDs[d] = true;
        });
        dataSources = Object.keys(newDs);

        const newDe: any = {};
        de.forEach((d) => {
          newDe[d] = true;
        });
        dataElements.forEach((d: any) => {
          newDe[d] = true;
        });
        dataElements = Object.keys(newDe);
      }

      if (dataSources.length) {
        const noRefresh: any = [];
        const withRefresh: any = [];
        const playerBased: any = [];

        dataSources.forEach((ds: any) => {
          if (ds === window.config.dataSourceAllJackpots) return;

          if (config.dataSources[ds] && config.dataSources[ds].target_player) {
            playerBased.push(ds);
          } else if (config.dataSources[ds] && config.dataSources[ds].enable_auto_refresh) {
            withRefresh.push(ds);
          } else {
            noRefresh.push(ds);
          }
        });

        //console.log('Fetching data sources for page', { noRefresh, withRefresh, playerBased, authenticationToken, id });

        if (playerBased?.length) dispatch(fetchSources({ ids: playerBased }));
        if (withRefresh?.length) dispatch(fetchSources({ ids: withRefresh }));
        if (noRefresh?.length) dispatch(fetchSources({ ids: noRefresh }));
      }
      if (dataElements.length) {
        // @ts-ignore
        dispatch(fetchElements({ ids: dataElements }));
      }

      if (dataSources.length && dataElements.length) {
        setDoneRender(1);
      } else {
        setDoneRender(2);
      }
    }
  }, [
    pattern,
    projectData,
    pagesOverrides,
    authenticationToken,
    config.dataSources,
    dispatch,
    config.pageLayouts,
    pageDataContext?.pageLayoutId,
    id,
  ]);

  React.useEffect(() => {
    if (doneRender === 2 && id) {
      try {
        window.setTimeout(() => {
          // @ts-ignore
          if (typeof window.sendDrEvent === 'function') {
            // @ts-ignore
            window.sendDrEvent({ type: 'render', detail: `page id ${id}` });
          }
        }, 10);
      } catch (e) {}
    }
  }, [doneRender, id]);

  React.useEffect(() => {
    if (doneRender === 1 && dataSourcesLoaded) {
      setDoneRender(2);
    }
  }, [doneRender, dataSourcesLoaded]);

  React.useEffect(() => {
    return () => {
      clearCache();
    };
  }, [id]);

  const data: any = React.useMemo(() => {
    if (!id || !projectData) return null;

    const rootNodeId = 'ROOT';
    const data = JSON.parse(projectData);

    const omittedKeys: Record<string, boolean> = { dsId: true, deId: true, dsType: true, expose: true };
    const rootProps: any = {};

    if (action?.redirect && config?.setPages?.['/maintenance/default']?.page_id) {
      const pageLayoutId = config.setPages['/maintenance/default'].page_layout_id;
      const pageId = config.setPages['/maintenance/default'].page_id;
      const overrides = pagesOverrides[`${pattern}|${pageId}`];

      if (overrides && Object.keys(overrides).length) {
        transferPropertiesToComponent(data, overrides, true, true, false);
      }

      Object.keys(data?.[rootNodeId]?.props?.properties ?? {}).forEach((key: any) => {
        if (typeof key !== 'string') key = key.toString();

        if (!omittedKeys[key]) {
          rootProps[key] = data[rootNodeId].props.properties[key];
        }
      });

      const content = craftJsParser({
        craftState: data,
        rootNodeId,
        pageLayoutId,
        pageId,
        pageType: 'page',
        options: {
          authenticated,
          overrideId: `${pattern}|${pageId}`,
        },
        withProvider: true,
      });

      return {
        content: <React.Fragment>{content}</React.Fragment>,
        isPage: false,
        contextValue: {
          ...rootProps,
          parentContext: pageDataContext,
          __id: id,
        },
      };
    } else if (page && page.a && !authenticated) {
      if (isAuthInProgress) {
        return { content: <PageLoader />, isPage: false, contextValue: {} };
      }

      if (config.setPages && config.setPages['/login']) {
        const pageLayoutId = config.setPages['/login'].page_layout_id;
        const pageId = config.setPages['/login'].page_id;
        const overrides = pagesOverrides[`${pattern}|${pageId}`];

        if (overrides && Object.keys(overrides).length) {
          transferPropertiesToComponent(data, overrides, true, true, false);
        }

        Object.keys(data?.[rootNodeId]?.props?.properties ?? {}).forEach((key: any) => {
          if (typeof key !== 'string') key = key.toString();

          if (!omittedKeys[key]) {
            rootProps[key] = data[rootNodeId].props.properties[key];
          }
        });

        const content = craftJsParser({
          craftState: data,
          rootNodeId,
          pageLayoutId,
          pageId,
          pageType: 'page',
          options: {
            authenticated,
            overrideId: `${pattern}|${pageId}`,
          },
          withProvider: true,
        });

        return { content: <React.Fragment>{content}</React.Fragment>, isPage: false };
      }
    }

    const overrides = pagesOverrides[`${pattern}|${id}`];

    if (overrides && Object.keys(overrides).length) {
      transferPropertiesToComponent(data, overrides, true, true, false);
    }

    Object.keys(data?.[rootNodeId]?.props?.properties ?? {}).forEach((key: any) => {
      if (typeof key !== 'string') key = key.toString();

      if (!omittedKeys[key]) {
        rootProps[key] = data[rootNodeId].props.properties[key];
      }
    });

    const content = craftJsParser({
      craftState: data,
      rootNodeId,
      pageId: id,
      pageType: 'page',
      options: {
        authenticated,
        overrideId: `${pattern}|${id}`,
      },
      withProvider: true,
    });

    let meta = null;
    if (page && page.pmd && Object.keys(page.pmd).length) {
      meta = <PageMetaTags meta={page.pmd} />;
    } else {
      // just in case this were set by a previous page with metadata but this current page doesn't have metadata
      document.documentElement.classList.remove('light-mode', 'dark-mode');
    }

    return {
      content,
      meta,
      isPage: true,
      contextValue: {
        ...rootProps,
        parentContext: pageDataContext,
        __id: id,
      },
    };
  }, [
    id,
    isAuthInProgress,
    authenticated,
    action,
    config,
    projectData,
    page,
    pagesOverrides,
    pattern,
    pageDataContext,
  ]);

  if (!data) {
    return null;
  }

  const useFixedContent = data.isPage && fixedContent != null && fixedContent.pageId === id;

  return (
    <DataElementContext.Provider value={data.contextValue}>
      {data.isPage ? data.meta : null}
      {useFixedContent ? fixedContent.content : data.content}
    </DataElementContext.Provider>
  );
};

export default Page;
